import {Injectable, NgZone} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, Subject, of, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {StartupService} from '../service-ui/startup.service';
import {MessageService} from '../service-ui/message.service';
import {NotificationsService} from './notifications.service';
import {EndpointCall} from './project-endpoints';
import {ProjectMappingEndpoints} from './project-mapping-endpoints';
import {
  ProjectMappingApplyRequest,
  ProjectMappingApplyResponse,
  ProjectMappingDashboard,
  ProjectMappingDashboardRow,
  ProjectMappingDecideRequest,
  ProjectMappingDecideResponse,
  ProjectMappingReview,
  ProjectMappingRunResponse,
  ProjectMappingRunSummary
} from '../../model/ProjectMapping';

/**
 * The Project-mapping backfill surfaces: an operator dashboard over
 * organizations and the per-organization mapping review.
 *
 * ## Why this is not an AbstractLiveDataService
 *
 * Every other service in this folder owns a cached collection of one model
 * keyed by `ID`, refreshed in place by notifications. None of that applies
 * here. A *proposal* is not an entity the rest of the app holds; it exists
 * only while a mapping run is being reviewed, it is replaced wholesale by a
 * re-plan, and its id space is per-run. Registering it with
 * `StartupService` would also load it at sign-in for every role, which is
 * the opposite of what is wanted -- this is an on-demand service in the
 * sense AGENTS.md describes, loaded when a screen asks for it.
 *
 * ## Errors are NOT swallowed
 *
 * `handleErrorX` in the older services turns a failure into `of(undefined)`,
 * which a subscriber cannot distinguish from "the server answered and there
 * was nothing". On this screen that would read as *"your organization needs
 * no mapping decisions"* -- the single most damaging wrong answer the surface
 * can give, because it closes the question. Every call here propagates its
 * error and every caller renders it.
 *
 * ## Live updates: key off the run, never off the item burst
 *
 * Applying an organization with 4,000 items publishes roughly 4,000
 * `BOOK`/`DRAFT` messages plus one `PROJECT` create per cluster
 * (`ProjectMappingReview.md` §7). Counting those to drive a progress bar
 * means 4,000 change-detection passes to render one number. The server
 * publishes a run-level `PROJECT_MAPPING_RUN` instead; `NotificationsService`
 * hands it to {@link runNotification}, subscribers re-read the summary, and
 * the per-item burst is ignored entirely.
 */
@Injectable()
export class ProjectMappingService {

  /** Emits whenever a `PROJECT_MAPPING_RUN` message arrives. */
  public readonly runUpdates: Subject<ProjectMappingRunSummary> = new Subject<ProjectMappingRunSummary>();

  public dashboardLoadingState: 'INITIAL' | 'LOADING' | 'LOADED' | 'ERROR' = 'INITIAL';
  public reviewLoadingState: 'INITIAL' | 'LOADING' | 'LOADED' | 'ERROR' = 'INITIAL';

  constructor(private http: HttpClient,
              private messageService: MessageService,
              private startupService: StartupService,
              private notificationsService: NotificationsService,
              private zone: NgZone) {
    // Register, but do NOT call startNotification(): the SQS poll is already
    // running by the time any mapping screen is reachable, and starting a
    // second one would double every message the session receives.
    this.notificationsService.setProjectMappingService(this);
  }

  private options() {
    return this.startupService.getHttpOptions();
  }

  private send<T>(action: string, call: EndpointCall, body?: any): Observable<T> {
    let request: Observable<any>;
    switch ( call.verb ) {
      case 'GET':    request = this.http.get<T>(call.url, this.options()); break;
      case 'DELETE': request = this.http.delete<T>(call.url, this.options()); break;
      case 'PUT':    request = this.http.put<T>(call.url, body || {}, this.options()); break;
      default:       request = this.http.post<T>(call.url, body || {}, this.options()); break;
    }
    return request.pipe(
      map(response => response as T),
      catchError((error: any) => {
        console.error(error, `projectMapping.${action}`);
        this.messageService.add(`projectMapping.${action} failed: ${error && error.message ? error.message : error}`);
        return throwError(error);
      })
    );
  }

  /**
   * Operator dashboard. One row per organization in scope -- all of them for
   * `network`, the service provider's own for `serviceProvider`.
   *
   * The scope is the server's, and it is deliberately NOT `Organizations.all()`:
   * that query's `serviceProvider` branch requires a PrintNodeOrder within
   * three months, which for mapping is backwards -- a dormant catalogue with
   * no recent print activity is exactly what needs mapping and would be
   * invisible. See `ProjectMappingReview.md` §2.
   */
  getDashboard(): Observable<ProjectMappingDashboardRow[]> {
    this.dashboardLoadingState = 'LOADING';
    return this.send<ProjectMappingDashboard>('dashboard', ProjectMappingEndpoints.dashboard(environment.apiBaseURL)).pipe(
      map(payload => {
        this.dashboardLoadingState = 'LOADED';
        return payload && payload.organizations ? payload.organizations : [];
      }),
      catchError(error => {
        this.dashboardLoadingState = 'ERROR';
        return throwError(error);
      })
    );
  }

  /**
   * The one review payload, for all three roles.
   *
   * An operator opening organization 412 and the customer at organization 412
   * get the identical response. That is the point of it: two implementations
   * drift, and they drift precisely where someone is already confused and on
   * the phone.
   */
  getReview(orgId: number): Observable<ProjectMappingReview> {
    this.reviewLoadingState = 'LOADING';
    return this.send<ProjectMappingReview>('review', ProjectMappingEndpoints.review(environment.apiBaseURL, orgId)).pipe(
      map(payload => {
        this.reviewLoadingState = 'LOADED';
        return payload;
      }),
      catchError(error => {
        this.reviewLoadingState = 'ERROR';
        return throwError(error);
      })
    );
  }

  /** Read-only. Creates no Project and writes no `work_ID`. */
  plan(orgId: number): Observable<ProjectMappingRunSummary> {
    return this.send<ProjectMappingRunSummary>('plan', ProjectMappingEndpoints.plan(environment.apiBaseURL, orgId));
  }

  /**
   * Records human answers. Writes nothing to `project`; idempotent and
   * re-sendable.
   *
   * **The response body matters and must not be discarded.** It was typed
   * `any` and thrown away until 2026-10-28, which hid two things the server
   * had always done: `EDITED` is stored in place of `ACCEPTED` whenever the
   * membership or a primary differs, and an entry naming a stale proposal is
   * returned in `rejected[]` while the others are still recorded. See
   * {@link ProjectMappingDecideResponse}.
   */
  decide(request: ProjectMappingDecideRequest): Observable<ProjectMappingDecideResponse> {
    return this.send<ProjectMappingDecideResponse>('decide', ProjectMappingEndpoints.decide(environment.apiBaseURL), request);
  }

  /**
   * §3.5. **Answers 202**: the run is enqueued and no Project exists yet.
   * Watch {@link getRun} for the phase; treating this body as a result shows
   * "done" before anything has been built.
   */
  apply(runId: string, request: ProjectMappingApplyRequest): Observable<ProjectMappingApplyResponse> {
    return this.send<ProjectMappingApplyResponse>('apply', ProjectMappingEndpoints.apply(environment.apiBaseURL, runId), request);
  }

  /**
   * §3.6. The authoritative phase, and the fallback for a session that never
   * receives the `PROJECT_MAPPING_RUN` message -- per `Notifications.md` §7
   * a queue is garbage collected after 15 minutes, so a burst can be missed
   * entirely.
   */
  getRun(runId: string): Observable<ProjectMappingRunResponse> {
    return this.send<ProjectMappingRunResponse>('run', ProjectMappingEndpoints.run(environment.apiBaseURL, runId));
  }

  /**
   * Operator only, on purpose. An organization that dislikes a result uses the
   * ordinary move and merge affordances it already understands; "undo an
   * entire backfill run" is not a concept a customer should have to hold.
   */
  revert(runId: string): Observable<ProjectMappingApplyResponse> {
    return this.send<ProjectMappingApplyResponse>('revert', ProjectMappingEndpoints.revert(environment.apiBaseURL, runId));
  }

  /**
   * Called by `NotificationsService` for a `PROJECT_MAPPING_RUN` message.
   *
   * Re-entered inside the Angular zone because the SQS poll runs outside it,
   * and a `Subject` emission from outside the zone updates the model without
   * scheduling change detection -- which looks exactly like the server not
   * having sent the message.
   */
  runNotification(object: any): void {
    if ( !object ) {
      return;
    }
    this.zone.run(() => {
      this.runUpdates.next(object as ProjectMappingRunSummary);
    });
  }

  /**
   * True when this role may plan, apply across an organization, and revert.
   * `organization` may decide and apply its own run but never plans or
   * reverts -- contract §2.
   */
  isOperator(): boolean {
    return this.startupService.selectedRole === 'network' || this.startupService.selectedRole === 'serviceProvider';
  }
}
